import { type LoyaltyUserUpdateInfo } from "@koala/sdk";
import { Formik } from "formik";
import styled from "styled-components";
import { lazy, object, string } from "yup";
import {
  StyledCancelAndSubmitButtons,
  StyledCancelEdit,
  StyledLinkButton,
} from "../styles";
import PrimaryButton from "@/components/checkout/uielements/primaryButton";
import { StyledFormField } from "@/components/uielements/form-fields/styles";
import { Form, Input } from "@/components/uielements/formik";
import {
  REGEX,
  REGEX_VALIDATION_STRINGS,
  VALIDATION_STRINGS,
} from "@/constants/validation";
import { useDispatch, useSelector } from "@/redux";
import meActions from "@/redux/me/actions";

interface Props {
  close: () => void;
}
export const ContactInfoForm = ({ close }: Props) => {
  const dispatch = useDispatch();
  const { data, loading } = useSelector((state) => state.app.me);
  const externalAuth = useSelector(
    (state) => state.app.organization.external_auth
  );
  const webConfig = useSelector((state) => state.app.cmsConfig.webConfig);
  const isZipCodeEnabledAndDisplayed =
    webConfig.accounts.zip_code !== "disabled" && data?.zip_code !== null;

  const updatableFields = externalAuth.updatable_fields.user;
  const readOnlyFields = externalAuth.readonly_fields.user;
  const initialValues: LoyaltyUserUpdateInfo = {
    first_name: data?.first_name ?? "",
    last_name: data?.last_name ?? "",
    email: data?.email ?? "",
    phone: data?.phone ?? "",
    zip_code: data?.zip_code ?? "",
  };

  // Validation schema for the reset form.
  const contactInfoSchema = object({
    first_name: string().required(VALIDATION_STRINGS.REQUIRED),
    last_name: string().required(VALIDATION_STRINGS.REQUIRED),
    email: string()
      .email(VALIDATION_STRINGS.EMAIL)
      .required(VALIDATION_STRINGS.REQUIRED),
    phone: string()
      .required(VALIDATION_STRINGS.REQUIRED)
      .matches(REGEX.PHONE, REGEX_VALIDATION_STRINGS.PHONE),
    zip_code: lazy(() =>
      isZipCodeEnabledAndDisplayed
        ? string()
            .required(VALIDATION_STRINGS.REQUIRED)
            .matches(REGEX.ZIP_CODE_USA_AND_POSTAL_CODE_CANADA, {
              message:
                REGEX_VALIDATION_STRINGS.ZIP_CODE_USA_AND_POSTAL_CODE_CANADA,
            })
        : string().optional()
    ),
  });

  const isFieldDisabled = (name: string) =>
    readOnlyFields.includes(name) || !updatableFields.includes(name);

  return (
    <Formik
      validateOnBlur
      validateOnChange={false}
      onSubmit={(formValues, { resetForm }) => {
        dispatch(meActions.updateMe(formValues));
        resetForm();
        close();
      }}
      initialValues={initialValues}
      validationSchema={contactInfoSchema}
    >
      {({ dirty, isValid }) => (
        <Form data-testid="contact-info-form">
          <StyledFormField>
            <Input
              label={""}
              id="first_name"
              name="first_name"
              placeholder="First Name"
              type="text"
              disabled={isFieldDisabled("first_name")}
            />
          </StyledFormField>

          <StyledFormField>
            <Input
              label={""}
              id="last_name"
              name="last_name"
              placeholder="Last Name"
              type="text"
              disabled={isFieldDisabled("last_name")}
            />
          </StyledFormField>

          <StyledFormField>
            <Input
              label={""}
              id="email"
              name="email"
              placeholder="Email"
              type="text"
              disabled={isFieldDisabled("email")}
            />
          </StyledFormField>

          <StyledFormField>
            <Input
              label={""}
              id="phone"
              name="phone"
              type="tel"
              placeholder="Phone Number"
              disabled={isFieldDisabled("phone")}
            />
          </StyledFormField>

          {isZipCodeEnabledAndDisplayed && data?.zip_code !== null ? (
            <StyledFormField>
              <Input
                label={""}
                id="zip_code"
                name="zip_code"
                type="text"
                placeholder="Zip Code"
                maxLength={10}
                disabled={isFieldDisabled("zip_code")}
              />
            </StyledFormField>
          ) : null}
          <CancelAndSubmitButtons>
            {/* Cancel button */}
            <StyledCancelEdit>
              <StyledLinkButton
                onClick={close}
                aria-label="Hide personal info form"
              >
                Cancel
              </StyledLinkButton>
            </StyledCancelEdit>
            {/* Submit Button */}
            <PrimaryButton
              type="submit"
              theme="secondary"
              submitting={loading}
              handleClick={() => null}
              minWidth={150}
              disabled={!dirty || !isValid}
              aria-label="Submit Updated Contact Info"
            >
              {loading ? "Loading" : "Submit"}
            </PrimaryButton>
          </CancelAndSubmitButtons>
        </Form>
      )}
    </Formik>
  );
};

const CancelAndSubmitButtons = styled(StyledCancelAndSubmitButtons)`
  margin-left: auto;
`;
